import React, {
  useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Box, Select, MenuItem, Button, TextField, Card, Checkbox, FormControlLabel,
} from '@mui/material';
import DoneIcon from '@mui/icons-material/Done';
import Table from '../components/Table';
import SettingsButton from '../components/SettingsButton';
import {
  getLocalStorageTablePref, updateTablePreferenceLocalStorage, tableColumnPreferenceLoader,
} from '../utils';
import {
  parseDonorConfigs, formatConfigChipLabel, formatConfigChipDetail, anyRuleMatchesFilters,
  TYPE_META, TYPE_FILTER_OPTIONS, CATEGORY_FILTER_OPTIONS, RECENCY_FILTER_OPTIONS,
  ANY_FILTER_VALUE, DEFAULT_CONFIG_FILTERS,
} from '../utils/deliveryConfig';

const TABLE_PREFIX = 'deliverysettings';

// Kept in sync with the matching literal in Table.jsx's row--detailPanelExpanded rule, so
// the expanded row and its detail panel read as one continuous highlighted block.
const EXPANDED_HIGHLIGHT_BG = '#F8FDFC';

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

const initColumnVisibilityModel = { vendor: false };

function ConfigChip({ rule }) {
  return (
    <Box
      sx={{
        display: 'inline-flex', alignItems: 'center', gap: '4px', width: 'fit-content',
      }}
      title={formatConfigChipLabel(rule)}
    >
      <Box
        data-testid="rule-rank-badge"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          bgcolor: rule.color,
          color: '#fff',
          fontSize: '0.65rem',
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {rule.rank}
      </Box>
      <Box
        sx={{
          padding: '2px 8px',
          borderRadius: '4px',
          bgcolor: rule.bg,
          color: rule.color,
          fontSize: '0.75rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
        }}
      >
        {formatConfigChipDetail(rule)}
      </Box>
    </Box>
  );
}

ConfigChip.propTypes = {
  rule: PropTypes.shape({
    bg: PropTypes.string,
    color: PropTypes.string,
    rank: PropTypes.number,
  }).isRequired,
};

const VISIBLE_CHIP_COUNT = 3;

function ConfigCell({ value = null }) {
  const rules = useMemo(() => parseDonorConfigs(value), [value]);
  if (!rules.length) {
    return (
      <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
        No configs set
      </Box>
    );
  }
  const visibleRules = rules.slice(0, VISIBLE_CHIP_COUNT);
  const extraCount = rules.length - visibleRules.length;
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap',
    }}
    >
      {visibleRules.map((rule) => (
        <ConfigChip key={`${rule.rank}-${rule.type}-${rule.category}-${rule.recencyDays}`} rule={rule} />
      ))}
      {extraCount > 0 && (
        <Box sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {`+${extraCount} more`}
        </Box>
      )}
    </Box>
  );
}

ConfigCell.propTypes = {
  value: PropTypes.string,
};

// Column order matches the existing "Email/Reactivation Donor Configuration" section on the
// Client Info page (AdAITab.jsx) so this read-only view reads the same way as the editable one.
const DETAIL_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: '110px 110px 90px 90px 50px',
  gap: '8px',
  alignItems: 'center',
};

function ConfigSection({ title, value = null }) {
  const rules = useMemo(() => parseDonorConfigs(value), [value]);
  return (
    <Box sx={{ minWidth: 460 }}>
      <Box sx={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: '8px' }}>{title}</Box>
      {!rules.length ? (
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', fontStyle: 'italic' }}>
          No configs set
        </Box>
      ) : (
        <Box>
          <Box sx={{
            ...DETAIL_GRID_SX,
            fontSize: '0.7rem',
            fontWeight: 700,
            color: 'text.secondary',
            paddingBottom: '4px',
            borderBottom: '1px solid #EEF0F2',
            marginBottom: '4px',
          }}
          >
            <span>Config Type</span>
            <span>Category</span>
            <span>Recency</span>
            <span>Proportion</span>
            <span>Rank</span>
          </Box>
          {rules.map((rule) => (
            <Box key={`${rule.rank}-${rule.type}-${rule.category}-${rule.recencyDays}`} sx={DETAIL_GRID_SX}>
              <Box sx={{ color: rule.color, fontSize: '0.8rem', fontWeight: 600 }}>{rule.typeLabel}</Box>
              <Box sx={{ fontSize: '0.8rem' }}>{rule.categoryLabel}</Box>
              <Box sx={{ fontSize: '0.8rem' }}>{rule.recencyLabel}</Box>
              <Box sx={{ fontSize: '0.8rem' }}>{`${rule.proportionPct}%`}</Box>
              <Box sx={{ fontSize: '0.8rem' }}>{rule.rank}</Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

ConfigSection.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.string,
};

function BooleanCell({ value = null }) {
  return value === 'true' ? <DoneIcon fontSize="small" color="success" /> : null;
}

BooleanCell.propTypes = {
  value: PropTypes.string,
};

// Legend for the config chip color/rank encoding, matching the design's "KEY" row.
function Legend() {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', fontSize: '0.75rem', color: 'text.secondary',
    }}
    >
      <Box sx={{ fontWeight: 700 }}>KEY</Box>
      {Object.values(TYPE_META).map((meta) => (
        <Box key={meta.label} sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Box sx={{
            width: '10px', height: '10px', borderRadius: '2px', bgcolor: meta.color,
          }}
          />
          <span>{meta.label}</span>
        </Box>
      ))}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '16px',
          height: '16px',
          borderRadius: '50%',
          bgcolor: '#4B5563',
          color: '#fff',
          fontSize: '0.65rem',
          fontWeight: 700,
        }}
        >
          1
        </Box>
        <span>= pipeline rank (rank 1 fires first)</span>
      </Box>
    </Box>
  );
}

function DeliverySettings({
  clients = [],
  isLoading = false,
  view = 'deliverysettings',
  handleViewChange,
  hasTDCAdminPermission = false,
  hasDataEngineerPermission = false,
  hasAnalystPermission = false,
}) {
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(initColumnVisibilityModel);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const [sortModel, setSortModel] = useState([{ field: 'fullname', sort: 'asc' }]);
  const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState(() => new Set());
  const [configFilters, setConfigFilters] = useState(DEFAULT_CONFIG_FILTERS);
  const [searchValue, setSearchValue] = useState('');
  const [tdcOnly, setTdcOnly] = useState(false);
  const hasActiveConfigFilter = configFilters.category !== ANY_FILTER_VALUE
    || configFilters.type !== ANY_FILTER_VALUE
    || configFilters.recency !== ANY_FILTER_VALUE;
  const hasSettingsPermission = hasTDCAdminPermission
    || hasDataEngineerPermission
    || hasAnalystPermission;

  const columns = useMemo(() => [
    // Same pattern as ClientList.jsx's action column: a pinned cog icon that navigates to
    // the client's settings page, rather than the whole row being clickable for navigation
    // (row click is reserved for toggling the rule-detail expand below).
    ...(hasSettingsPermission ? [{
      field: 'settings',
      type: 'actions',
      headerName: 'Edit',
      width: 50,
      disableClickEventBubbling: true,
      cellClassName: 'settings',
      getActions: ({ id }) => [
        <SettingsButton key="settings" id={id} />,
      ],
    }] : []),
    {
      field: 'fullname',
      headerName: 'Client',
      minWidth: 200,
      flex: 1,
    },
    { field: 'sector', headerName: 'Sector', minWidth: 160 },
    { field: 'clienttype', headerName: 'Client Type', minWidth: 120 },
    {
      field: 'adaimember',
      headerName: 'TDC Member',
      minWidth: 110,
      renderCell: (params) => <BooleanCell value={params.value} />,
    },
    {
      field: 'expandedwindow',
      headerName: 'Window',
      minWidth: 90,
      renderCell: (params) => <BooleanCell value={params.value} />,
    },
    { field: 'vendor', headerName: 'Vendor', minWidth: 140 },
    {
      field: 'emailconfigurations',
      headerName: 'Email Configurations',
      minWidth: 260,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => <ConfigCell value={params.value} />,
    },
    {
      field: 'smsconfigurations',
      headerName: 'SMS Configurations',
      minWidth: 260,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => <ConfigCell value={params.value} />,
    },
    {
      field: 'reactivationconfigurations',
      headerName: 'Reactivation Configurations',
      minWidth: 260,
      flex: 1,
      sortable: false,
      filterable: false,
      renderCell: (params) => <ConfigCell value={params.value} />,
    },
  ], [hasSettingsPermission]);

  const [orderedColumns, setOrderedColumns] = useState(columns);

  const getDefaultColumns = () => columns;
  const customSetOrdered = (neworder) => {
    updateTablePreferenceLocalStorage(TABLE_PREFIX, 'column', neworder);
  };

  useEffect(() => {
    document.title = 'MissionPortal | Delivery Settings';
    const savedFilter = getLocalStorageTablePref(TABLE_PREFIX, 'filter');
    if (savedFilter) {
      setFilterModel(savedFilter);
    }
    const savedSort = getLocalStorageTablePref(TABLE_PREFIX, 'sort');
    if (savedSort) {
      setSortModel(savedSort);
    }
    const savedVisibility = getLocalStorageTablePref(TABLE_PREFIX, 'column-visibility');
    if (savedVisibility) {
      setColumnVisibilityModel(savedVisibility);
    }
  }, []);

  useEffect(() => {
    tableColumnPreferenceLoader(TABLE_PREFIX, 'column', columns, setOrderedColumns);
  }, [columns]);

  const filteredClients = useMemo(() => {
    const searchRegex = searchValue ? new RegExp(escapeRegExp(searchValue), 'i') : null;
    return clients.filter((c) => {
      const matchesSearch = !searchRegex
        || searchRegex.test(c.fullname || '')
        || searchRegex.test(c.code || '');
      const matchesConfig = !hasActiveConfigFilter || anyRuleMatchesFilters(
        [c.emailconfigurations, c.smsconfigurations, c.reactivationconfigurations],
        configFilters,
      );
      const matchesTdc = !tdcOnly || c.adaimember === 'true';
      return matchesSearch && matchesConfig && matchesTdc;
    });
  }, [clients, searchValue, configFilters, hasActiveConfigFilter, tdcOnly]);

  const pageState = useMemo(
    () => ({ data: filteredClients, isLoading }),
    [filteredClients, isLoading],
  );

  const handleRowClick = (params) => {
    setDetailPanelExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(params.id)) {
        next.delete(params.id);
      } else {
        next.add(params.id);
      }
      return next;
    });
  };

  const getDetailPanelContent = ({ row }) => (
    <Card
      variant="outlined"
      square
      sx={{
        display: 'flex',
        gap: 4,
        p: 3,
        borderLeft: 0,
        borderRight: 0,
        borderRadius: 0,
        bgcolor: EXPANDED_HIGHLIGHT_BG,
      }}
    >
      <ConfigSection title="Email Configurations" value={row.emailconfigurations} />
      <ConfigSection title="SMS Configurations" value={row.smsconfigurations} />
      <ConfigSection title="Reactivation Configurations" value={row.reactivationconfigurations} />
    </Card>
  );

  return (
    <div data-testid="delivery-settings">
      <div id="clientlist-header">
        <div className="header-left">
          <h2>Delivery Settings</h2>
        </div>
        <div className="header-right">
          <Select value={view} onChange={handleViewChange} size="small">
            <MenuItem value="clientlist">Client List</MenuItem>
            <MenuItem value="clientidentities">Client Identities</MenuItem>
            <MenuItem value="deliverysettings">Delivery Settings</MenuItem>
          </Select>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
          />
        </div>
      </div>
      <Legend />
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', margin: '12px 0',
      }}
      >
        <Box sx={{ fontWeight: 700, fontSize: '0.8rem', color: 'text.secondary' }}>
          FILTER CONFIGS
        </Box>
        <FormControlLabel
          control={(
            <Checkbox
              size="small"
              checked={tdcOnly}
              onChange={(e) => setTdcOnly(e.target.checked)}
            />
          )}
          label="TDC members only"
        />
        <Select
          size="small"
          value={configFilters.category}
          onChange={(e) => setConfigFilters((prev) => ({ ...prev, category: e.target.value }))}
          displayEmpty
          renderValue={(v) => (v === ANY_FILTER_VALUE ? 'Category' : CATEGORY_FILTER_OPTIONS.find((o) => o.value === v)?.label)}
        >
          <MenuItem value={ANY_FILTER_VALUE}>Any Category</MenuItem>
          {CATEGORY_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={configFilters.type}
          onChange={(e) => setConfigFilters((prev) => ({ ...prev, type: e.target.value }))}
          displayEmpty
          renderValue={(v) => (v === ANY_FILTER_VALUE ? 'Type' : TYPE_FILTER_OPTIONS.find((o) => o.value === v)?.label)}
        >
          <MenuItem value={ANY_FILTER_VALUE}>Any Type</MenuItem>
          {TYPE_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        <Select
          size="small"
          value={configFilters.recency}
          onChange={(e) => setConfigFilters((prev) => ({ ...prev, recency: e.target.value }))}
          displayEmpty
          renderValue={(v) => (v === ANY_FILTER_VALUE ? 'Recency' : RECENCY_FILTER_OPTIONS.find((o) => o.value === v)?.label)}
        >
          <MenuItem value={ANY_FILTER_VALUE}>Any Recency</MenuItem>
          {RECENCY_FILTER_OPTIONS.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </Select>
        {hasActiveConfigFilter && (
          <Button size="small" onClick={() => setConfigFilters(DEFAULT_CONFIG_FILTERS)}>
            Clear
          </Button>
        )}
        <Box sx={{ flex: 1 }} />
        <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
          {`${filteredClients.length} clients`}
        </Box>
      </Box>
      <Table
        rowClickFunc={handleRowClick}
        pageState={pageState}
        updatePageState={() => {}}
        columns={orderedColumns}
        sortModel={sortModel}
        setSortModel={setSortModel}
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        filterModel={filterModel}
        setFilterModel={setFilterModel}
        pinnedColumns={{
          pinnedColumns: {
            left: hasSettingsPermission ? ['settings', 'fullname'] : ['fullname'],
          },
        }}
        tablePrefix={TABLE_PREFIX}
        setOrderedColumns={setOrderedColumns}
        getDefaultColumns={getDefaultColumns}
        customSetOrdered={customSetOrdered}
        getDetailPanelContent={getDetailPanelContent}
        detailPanelExpandedRowIds={detailPanelExpandedRowIds}
        onDetailPanelExpandedRowIdsChange={setDetailPanelExpandedRowIds}
      />
    </div>
  );
}

export default DeliverySettings;

DeliverySettings.propTypes = {
  clients: PropTypes.oneOfType([PropTypes.array]),
  isLoading: PropTypes.bool,
  view: PropTypes.string,
  handleViewChange: PropTypes.func.isRequired,
  hasTDCAdminPermission: PropTypes.bool,
  hasDataEngineerPermission: PropTypes.bool,
  hasAnalystPermission: PropTypes.bool,
};
